import { useCallback, useMemo } from 'react';
import { useMatches, useNavigate, useSearchParams } from 'react-router-dom';
import { type RegistrationRouteName } from '@/registration/pages';
import { generatePageUrl } from '@/app/pages';
import { useRegisterRoute } from './useRegisterRoute';
import { type RegistrationSteps } from '@/api/registration';
import { useGroupRegistrationQuery } from './useGroupRegistrationQuery';

export interface RegisterStep {
  routeName: RegistrationRouteName
  isEnable?: boolean
}

export function useRegisterGroupSteps() {
  const { tenantCode, eventId } = useRegisterRoute();
  const groupRegistrationQuery = useGroupRegistrationQuery(eventId);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const steps = useMemo(
    () => { return groupRegistrationQuery?.data?.groupRegistrationSteps ?? []; },
    [groupRegistrationQuery?.data?.groupRegistrationSteps]
  );

  const matches = useMatches();
  const currentStepRouteName = useMemo(
    () => { return matches[matches.length - 1].id as RegistrationRouteName; }
    , [matches]);

  const stepIndex = useMemo(
    () => { return steps.findIndex((currentStep) => { return currentStep.routeName === currentStepRouteName; }); }
    , [currentStepRouteName, steps]);

  const currentStep = useMemo(() => { return steps[stepIndex]; }, [stepIndex, steps]);

  const isReviewing = useMemo(() => { return !!searchParams.get('review'); }, [searchParams]);

  const findNextStep = useCallback(() => {
    const nextStep = steps[stepIndex + 1];
    return nextStep;
  }, [stepIndex, steps]);

  const findPreviousStep = useCallback(() => {
    if (stepIndex === -1) return null;

    const prevStep = steps[stepIndex - 1];
    return prevStep;
  }, [stepIndex, steps]);

  const toNextStep = useCallback(({ additionalParams } = {} as any) => {
    const nextStep = findNextStep();
    if (nextStep) {
      let params = Object.fromEntries(searchParams);
      if (additionalParams) {
        params = { ...params, ...additionalParams };
      }
      navigate(generatePageUrl(nextStep.routeName, { tenantCode, eventId }, params));
    }
  }, [eventId, findNextStep, navigate, searchParams, tenantCode]);

  // TODO: toNextStep not use latest data, need to fix and remove toNextStepWithSteps
  const toNextStepWithSteps = useCallback((registrationSteps: RegistrationSteps) => {
    const nextStep = registrationSteps[stepIndex + 1];
    if (nextStep) {
      navigate(generatePageUrl(nextStep.routeName, { tenantCode, eventId }, Object.fromEntries(searchParams)));
    }
  }, [eventId, navigate, searchParams, stepIndex, tenantCode]);

  const generateNextStepUrl = useCallback((additionalParams: object) => {
    const nextStep = findNextStep();
    const params = { ...Object.fromEntries(searchParams), ...additionalParams };
    return generatePageUrl(nextStep.routeName, { tenantCode, eventId }, params);
  }, [eventId, findNextStep, searchParams, tenantCode]);

  const generatePaymentProcessingStepUrl = useCallback(
    (additionalParams: object) => {
      const nextStep = {
        routeName:
          'RegistrationRegisterGroupPaymentProcessing' as RegistrationRouteName,
        isEnable: true,
      };
      const params = {
        ...Object.fromEntries(searchParams),
        ...additionalParams,
      };
      return generatePageUrl(
        nextStep.routeName,
        { tenantCode, eventId },
        params
      );
    },
    [eventId, searchParams, tenantCode]
  );

  const toPaymentProcessingStep = useCallback(
    (additionalParams: object) => {
      const nextStep = generatePaymentProcessingStepUrl(additionalParams);
      navigate(nextStep);
    },
    [generatePaymentProcessingStepUrl, navigate]
  );

  const toPreviousStep = useCallback(() => {
    const previousStep = findPreviousStep();
    if (previousStep) {
      navigate(generatePageUrl(previousStep.routeName, { tenantCode, eventId }, Object.fromEntries(searchParams)));
    }
  }, [eventId, findPreviousStep, navigate, searchParams, tenantCode]);

  return {
    steps,
    currentStep,
    isReviewing,
    stepIndex,
    findNextStep,
    findPreviousStep,
    toNextStep,
    toNextStepWithSteps,
    generatePaymentProcessingStepUrl,
    toPaymentProcessingStep,
    generateNextStepUrl,
    toPreviousStep
  };
}
